import { useParams, Link, useNavigate } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <main className="min-h-screen bg-background-50">
          <Navbar />
          <section className="pt-32 pb-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-bold font-heading text-foreground-950 mb-3">
              文章未找到
            </h1>
            <p className="text-foreground-600 mb-6">
              您访问的文章不存在或已被移除
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all duration-200 whitespace-nowrap text-sm"
            >
              返回首页
            </button>
          </section>
          <Footer />
        </main>
      </>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const articleSchema = {
    '@context': 'https://culturepan.github.io',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '潘金莲文化研究中心',
      url: origin,
    },
    url: currentUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': currentUrl,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://culturepan.github.io',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: origin,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category?.name || '分类',
        item: `${origin}/category/${category?.slug || ''}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      {article.tags.map((tag, i) => (
        <meta key={i} name="keywords" content={tag} />
      ))}

      <main className="min-h-screen bg-background-50">
        <Navbar />

        <article className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '分类', href: `/category/${category?.slug || ''}` },
                { label: article.title },
              ]}
            />

            {/* Article Header */}
            <header className="mt-6 mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="text-xs font-medium px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-sm text-foreground-500">{article.readTime}</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span className="text-foreground-300">|</span>
                <span>{article.publishDate}</span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="w-full aspect-[2/1] rounded-xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 潘金莲文化研究中心`}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Article Content */}
            <div
              className="article-content max-w-none"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 mr-2">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <ArticleCard key={related.id} {...related} />
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </main>
    </>
  );
}